import { ImagePreviewItem } from '@zayan-erp/shared';
import {
  DsmAddendumEnvelope,
  DsmEnvelopeStatus,
  DsmSignOffSigner,
  DsmSignOffTagSeverity,
  DsmSigningTypeName
} from './dsm-addendum-sign-off.models';
import { DsmAppointmentStatus } from './dsm-appointment.models';
import { DsmDealLite } from './dsm-aps.models';
import { DsmModelElevationProductApiModel, DsmProductOption, DsmSelectedProductEntry } from './dsm-deal-options.models';
import { DiscountType } from './dsm-deal-options-view.models';
import { DsmProductLibraryImage } from './dsm-product-library.models';
import { DsmOptionTax } from './dsm-selection-basket.models';

export enum DsmContractDetailsTab {
  Steps = 'steps',
  Packages = 'packages',
  Documents = 'documents',
  Notes = 'notes'
}

export const DSM_CONTRACT_DETAILS_TAB_LABELS: Record<DsmContractDetailsTab, string> = {
  [DsmContractDetailsTab.Steps]: 'Appointment Steps',
  [DsmContractDetailsTab.Packages]: 'Packages',
  [DsmContractDetailsTab.Documents]: 'Documents',
  [DsmContractDetailsTab.Notes]: 'Notes'
};

export enum DsmContractOptionSort {
  Code = 'code',
  PriceAsc = 'price-asc',
  PriceDesc = 'price-desc'
}

export const DSM_CONTRACT_OPTION_SORT_LABELS: Record<DsmContractOptionSort, string> = {
  [DsmContractOptionSort.Code]: 'Sorted by code',
  [DsmContractOptionSort.PriceAsc]: 'Price: low to high',
  [DsmContractOptionSort.PriceDesc]: 'Price: high to low'
};

export enum DsmContractFinalizeStep {
  Review = 'review',
  Save = 'save'
}

export enum DsmContractAddendumTarget {
  New = 'new',
  Existing = 'existing'
}

export enum DsmContractNoteEntryKind {
  Day = 'day',
  Note = 'note'
}

export enum DsmContractDetailsQueryParam {
  AppointmentId = 'appointmentId'
}

export interface DsmContractDetails {
  deal: DsmDealLite;
  selectionsTotal: number;
  selectionsInContractTotal: number;
}

export interface DsmDealStep {
  stepId: string;
  name: string;
  sortOrder: number;
  image: DsmProductLibraryImage | null;
  scenarioId: string;
  scenarioName: string;
  subcategories: DsmDealStepSubcategory[];
}

export interface DsmDealStepQuery {
  subcategoryId: string | null;
  search: string | null;
}

export interface DsmDealStepSubcategory {
  subcategoryId: string;
  code: string;
  label: string;
  image: DsmProductLibraryImage | null;
  products: DsmModelElevationProductApiModel[];
}

export enum DsmContractDocumentType {
  Aps = 'APS',
  Amendment = 'AMENDMENT',
  Addendum = 'ADDENDUM',
  Custom = 'CUSTOM'
}

export const DSM_CONTRACT_DOCUMENT_TYPE_LABELS: Record<DsmContractDocumentType, string> = {
  [DsmContractDocumentType.Aps]: 'Agreement of Purchase and Sale',
  [DsmContractDocumentType.Amendment]: 'Amendment',
  [DsmContractDocumentType.Addendum]: 'Addendum',
  [DsmContractDocumentType.Custom]: 'Custom Document'
};

export interface DsmContractDocument {
  id: string;
  documentType: DsmContractDocumentType;
  status: DsmEnvelopeStatus;
  signingType: DsmSigningTypeName;
  templateName: string | null;
  signedS3DocumentId: string | null;
  createdAt: string;
  createdByName: string | null;
  dealDocumentSignatures: DsmSignOffSigner[];
}

export interface DsmContractExternalSigning {
  id: string;
  note: string | null;
  createdAt: string;
  doumentInfo: DsmProductLibraryImage | null;
}

export interface DsmContractDocuments {
  documents: DsmContractDocument[];
  externalSigning: DsmContractExternalSigning | null;
}

export interface DsmContractHeaderDetail {
  label: string;
  href: string | null;
}

export interface DsmContractDetailsBackLink {
  link: string[];
  queryParams: Record<string, string | null> | null;
}

export interface DsmContractStepCard {
  stepId: string;
  name: string;
  position: number;
  imageUrl: string | null;
}

export interface DsmContractStepGroup {
  scenarioId: string;
  scenarioName: string;
  steps: DsmContractStepCard[];
}

export interface DsmContractStepOption extends DsmProductOption {
  subcategoryId: string;
  subcategoryLabel: string;
  imageUrl: string | null;
  previewImages: ImagePreviewItem[];
}

export interface DsmContractStepOptionCard {
  option: DsmContractStepOption;
  selectedCount: number;
}

export interface DsmContractStatusTag {
  label: string;
  severity: DsmSignOffTagSeverity;
}

export const DSM_CONTRACT_SIGNED_TAG: DsmContractStatusTag = { label: 'Signed', severity: 'success' };

export const DSM_CONTRACT_PENDING_TAG: DsmContractStatusTag = { label: 'Pending', severity: 'warn' };

export const DSM_CONTRACT_APPOINTMENT_IN_PROGRESS_TAG: DsmContractStatusTag = {
  label: 'In Progress',
  severity: 'info'
};

export const DSM_CONTRACT_APPOINTMENT_STATUS_SEVERITIES: Partial<Record<DsmAppointmentStatus, DsmSignOffTagSeverity>> =
  {
    [DsmAppointmentStatus.Completed]: 'success',
    [DsmAppointmentStatus.Cancelled]: 'danger',
    [DsmAppointmentStatus.NoShow]: 'danger',
    [DsmAppointmentStatus.ReschedulingNeeded]: 'warn'
  };

export interface DsmContractDocumentRow {
  document: DsmContractDocument;
  title: string;
  status: DsmContractStatusTag;
  canDownload: boolean;
  envelope: DsmAddendumEnvelope | null;
}

export interface DsmContractExternalSigningRow {
  title: string;
  url: string | null;
}

export interface DsmContractTrackerRow {
  id: string;
  code: string;
  description: string;
  unitPrice: number;
  uom: string;
  quantity: number;
  amount: number;
  isCancelled: boolean;
  isPending: boolean;
  addendumLabel: string;
  isAddendumLocked: boolean;
  isAddendumCancelled: boolean;
}

export interface DsmContractSelection extends DsmSelectedProductEntry {
  stepId: string | null;
  stepName: string | null;
  subcategoryLabel: string | null;
  locationLabel: string | null;
  attributeLabels: string[];
  discountType: DiscountType;
  discountValue: number;
  error: string | null;
}

export type DsmContractSelectionLine = Pick<
  DsmContractSelection,
  'values' | 'locationLabel' | 'attributeLabels' | 'discountType' | 'discountValue'
>;

export interface DsmContractOptionConfigRequest {
  product: DsmProductOption;
  stepId: string | null;
  stepName: string | null;
  subcategoryLabel: string | null;
  selection: DsmContractSelection | null;
}

export interface DsmContractSelectionCard {
  selection: DsmContractSelection;
  imageUrl: string | null;
  pathLabel: string;
  detailLabel: string;
  gross: number;
  discount: number;
  net: number;
  unitNet: number;
}

export interface DsmContractSelectionGroup {
  key: string;
  label: string | null;
  countLabel: string;
  cards: DsmContractSelectionCard[];
}

export interface DsmContractSelectionTotals {
  gross: number;
  optionDiscount: number;
  optionDiscountCount: number;
  globalDiscount: number;
  taxes: DsmOptionTax[];
  taxesTotal: number;
  incentive: number;
  net: number;
  savings: number;
  // false while the selection basket preview is unavailable: the figures are then worked out here, before tax
  includesTax: boolean;
}

export interface DsmContractStepReview {
  stepId: string;
  scenarioId: string;
  isReviewed: boolean;
}

export interface DsmContractReviewedSteps {
  reviewedStepCount: number;
  stepCount: number;
  steps: DsmContractStepReview[];
}

export interface DsmContractStepReviewPayload {
  isReviewed: boolean;
}

export interface DsmContractNoteEntry {
  key: string;
  kind: DsmContractNoteEntryKind;
  label: string;
  noteId: string | null;
  createdAt: string | null;
  voiceUrl: string | null;
  voiceDocumentId: string | null;
  transcription: string | null;
  canEdit: boolean;
}

export interface DsmContractNotePlayback {
  noteId: string;
  progress: number;
}
